
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Star, Info, Clock, Globe, Share2, Bookmark, Mountain } from "lucide-react";
import { ReviewList } from "@/components/reviews/ReviewList";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { ReviewItemProps } from "@/components/reviews/ReviewItem";

const DestinationDetail = () => {
  const { id } = useParams();
  const [destination, setDestination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviews, setReviews] = useState<ReviewItemProps[]>([]);

  useEffect(() => {
    // Simulate fetching destination data
    const fetchDestination = () => {
      setLoading(true);
      
      // Mock destination data
      const destinations = [
        {
          id: "1",
          name: "그랜드 캐니언",
          image: "https://images.unsplash.com/photo-1472396961693-142e6e269027?w=800&h=500&fit=crop",
          gallery: [
            "https://images.unsplash.com/photo-1505852679233-d9fd70aff56d?w=800&h=500&fit=crop",
            "https://images.unsplash.com/photo-1575527048015-363ad7c38906?w=800&h=500&fit=crop",
            "https://images.unsplash.com/photo-1527440421615-5a578f29eb88?w=800&h=500&fit=crop",
          ],
          category: "자연 명소",
          rating: 4.9,
          reviews: 312,
          location: "애리조나",
          address: "Grand Canyon National Park, AZ 86023",
          hours: "국립공원으로 연중무휴, 방문자 센터는 8:00 AM - 5:00 PM",
          website: "https://www.nps.gov/grca/",
          description: "세계에서 가장 유명한 자연 명소인 그랜드 캐니언은 라스베가스에서 당일치기로 방문할 수 있습니다. 수백만 년에 걸쳐 콜로라도 강이 만들어낸 웅장한 협곡으로, 다양한 전망대에서 숨막히는 풍경을 감상할 수 있습니다. 하이킹, 래프팅, 헬리콥터 투어 등 다양한 활동을 즐길 수 있는 곳입니다.",
          badges: ["인기", "필수 방문"],
          travelTime: "라스베가스에서 차로 약 4시간 30분",
          bestTime: "봄(3월-5월)과 가을(9월-11월)",
          activities: [
            "경치 감상",
            "하이킹",
            "헬리콥터 투어",
            "래프팅",
            "사진 촬영"
          ],
          tips: [
            "방문 전 날씨 확인하기",
            "물과 간식 챙기기",
            "좋은 신발 준비하기",
            "선블록 바르기",
            "일출/일몰 시간에 방문하면 더 아름다운 풍경을 볼 수 있음"
          ],
          visitorReviews: [
            {
              author: "박민준",
              avatar: "",
              rating: 5,
              date: new Date("2023-10-10"),
              content: "말로 표현할 수 없는 장관입니다. 일출 시간에 방문했는데, 그 풍경은 평생 잊지 못할 거예요. 라스베가스에서 거리가 있지만 절대 후회하지 않을 여행지입니다."
            },
            {
              author: "이하은",
              avatar: "",
              rating: 4,
              date: new Date("2023-09-15"),
              content: "정말 아름다운 곳입니다. 다만 여름에 방문했더니 너무 더워서 오래 머물지 못했어요. 봄이나 가을에 방문하는 것을 추천합니다. 그래도 풍경은 정말 장관이었습니다."
            },
            {
              author: "정우진",
              avatar: "",
              rating: 5,
              date: new Date("2023-08-22"),
              content: "헬리콥터 투어를 했는데, 돈이 아깝지 않을 만큼 멋진 경험이었습니다. 위에서 내려다보는 그랜드 캐니언은 정말 압도적입니다. 강력 추천합니다!"
            }
          ]
        },
        {
          id: "2",
          name: "레드 락 캐니언",
          image: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=500&fit=crop",
          gallery: [
            "https://images.unsplash.com/photo-1433477155337-9aea4e790195?w=800&h=500&fit=crop",
            "https://images.unsplash.com/photo-1465188162913-8fb5709d6d57?w=800&h=500&fit=crop",
            "https://images.unsplash.com/photo-1439853949127-fa647821eba0?w=800&h=500&fit=crop",
          ],
          category: "자연 명소",
          rating: 4.7,
          reviews: 189,
          location: "라스베가스 근교",
          address: "Red Rock Canyon, Las Vegas, NV 89161",
          hours: "매일 6:00 AM - 8:00 PM (계절에 따라 변동)",
          website: "https://www.redrockcanyonlv.org/",
          description: "라스베가스에서 불과 30분 거리에 위치한 레드 락 캐니언은 붉은 바위와 아름다운 풍경으로 유명한 자연 보호구역입니다. 13마일의 경치 좋은 드라이브 루트를 따라 다양한 전망대와 하이킹 트레일을 만날 수 있습니다. 등산, 바위 등반, 마운틴 바이킹 등의 활동을 즐길 수 있으며, 도시의 번잡함에서 벗어나 자연을 만끽할 수 있는 곳입니다.",
          badges: ["하이킹", "사진 명소"],
          travelTime: "라스베가스에서 차로 약 30분",
          bestTime: "봄(3월-5월)과 가을(9월-11월)",
          activities: [
            "경치 드라이브",
            "하이킹",
            "바위 등반",
            "마운틴 바이킹",
            "피크닉"
          ],
          tips: [
            "주차 공간이 제한적이므로 이른 아침에 방문하는 것이 좋음",
            "입장료가 있으니 현금 준비",
            "충분한 물 가져가기",
            "날씨가 더운 날은 피하고 이른 아침이나 늦은 오후에 방문하는 것이 좋음"
          ],
          visitorReviews: [
            {
              author: "김태현",
              avatar: "",
              rating: 5,
              date: new Date("2023-10-05"),
              content: "라스베가스 여행 중 가장 좋았던 곳입니다. 도시의 화려함과는 다른 자연의 아름다움을 느낄 수 있어요. 하이킹 코스도 다양해서 취향에 맞게 즐길 수 있습니다."
            },
            {
              author: "최윤서",
              avatar: "",
              rating: 4,
              date: new Date("2023-09-22"),
              content: "라스베가스에서 가까워서 부담 없이 다녀올 수 있어요. 붉은 바위 풍경이 정말 인상적입니다. 사진 찍기 좋은 명소가 많아요."
            }
          ]
        }
      ];
      
      const found = destinations.find(d => d.id === id);
      
      if (found) {
        setDestination(found);
      }
      
      setLoading(false);
    };
    
    fetchDestination();
  }, [id]);

  const handleReviewSubmit = (rating: number, content: string) => {
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      const newReview: ReviewItemProps = {
        author: "사용자", // In a real app, this would be the logged-in user
        avatar: "",
        rating,
        date: new Date(),
        content
      };
      
      setReviews(prev => [newReview, ...prev]);
      setIsSubmitting(false);
    }, 1000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex justify-center items-center h-96">
            <div className="animate-pulse text-primary">로딩 중...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!destination) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-4">여행지를 찾을 수 없습니다</h2>
            <Button asChild>
              <Link to="/travel">맛집/여행지 목록으로 돌아가기</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Combine mock reviews with user-added reviews
  const allReviews = [...(destination.visitorReviews || []), ...reviews];

  return (
    <Layout>
      <div className="container py-8">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/travel" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
              <ArrowLeft className="h-4 w-4" />
              <span>맛집/여행지 목록으로 돌아가기</span>
            </Link>
          </Button>
          <h1 className="text-3xl font-bold mb-1">{destination.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-muted-foreground mb-4">
            <Badge className="bg-primary text-white">{destination.category}</Badge>
            <div className="flex items-center gap-1 text-amber-500">
              <Star className="h-4 w-4 fill-current" />
              <span className="font-medium">{destination.rating}</span>
              <span className="text-muted-foreground text-sm">({destination.reviews} 리뷰)</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">{destination.location}</span>
            </div>
          </div>
        </div>

        {/* Main image and gallery */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-2">
            <img 
              src={destination.image} 
              alt={destination.name} 
              className="w-full h-80 object-cover rounded-lg"
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            {destination.gallery?.slice(0, 3).map((img: string, index: number) => (
              <img 
                key={index} 
                src={img} 
                alt={`${destination.name} ${index+1}`} 
                className="w-full h-24 object-cover rounded-lg"
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Left column - Info */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold mb-4">상세 정보</h2>
            <p className="mb-6 text-muted-foreground">{destination.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-medium mb-3">방문 정보</h3>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                      <span>{destination.address}</span>
                    </div>
                    <div className="flex gap-3">
                      <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
                      <span>{destination.hours}</span>
                    </div>
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-muted-foreground shrink-0" />
                      <span>{destination.travelTime}</span>
                    </div>
                    {destination.website && (
                      <div className="flex gap-3">
                        <Globe className="h-5 w-5 text-muted-foreground shrink-0" />
                        <a 
                          href={destination.website} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-primary hover:underline"
                        >
                          공식 웹사이트
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-medium mb-3">특징</h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {destination.badges?.map((badge: string, index: number) => (
                      <Badge 
                        key={index} 
                        className="bg-muted text-muted-foreground hover:bg-muted"
                      >
                        {badge}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full flex items-center justify-center gap-2" 
                      onClick={() => {}}
                    >
                      <Share2 className="h-4 w-4" />
                      공유하기
                    </Button>
                    <Button 
                      variant="secondary" 
                      className="w-full flex items-center justify-center gap-2" 
                      onClick={() => {}}
                    >
                      <Bookmark className="h-4 w-4" />
                      저장하기
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Activities and tips section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <h2 className="text-xl font-semibold mb-4">추천 활동</h2>
                <Card>
                  <CardContent className="p-5">
                    <ul className="space-y-2">
                      {destination.activities?.map((activity: string, index: number) => (
                        <li key={index} className="flex items-center gap-2">
                          <Mountain className="h-4 w-4 text-primary" />
                          <span>{activity}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-4">여행 팁</h2>
                <Card>
                  <CardContent className="p-5">
                    <ul className="space-y-2">
                      {destination.tips?.map((tip: string, index: number) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-primary font-medium shrink-0">·</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 text-sm text-muted-foreground">
                      <span className="font-medium">방문 최적 시기:</span> {destination.bestTime}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div className="space-y-6">
              <ReviewList reviews={allReviews} title="방문자 후기" />
              
              <div className="mt-6">
                <ReviewForm onSubmit={handleReviewSubmit} isSubmitting={isSubmitting} />
              </div>
            </div>
          </div>
          
          {/* Right column - Map placeholder */}
          <div>
            <Card>
              <CardContent className="p-5">
                <h3 className="font-medium mb-3">위치</h3>
                <div className="bg-muted h-64 flex items-center justify-center rounded-md mb-4">
                  <MapPin className="h-8 w-8 text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">지도 영역</span>
                </div>
                <Button className="w-full">길찾기</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DestinationDetail;
